import { SOURCE_UI_TABS_REFRESH } from './config';

export function createLockProjection({
  hostAdapter,
  findLoadedEntities,
  getSampleInfoForReactionId,
  normalizeReviewState,
}) {
  const updateTabs = () => {
    hostAdapter.refreshTabs(SOURCE_UI_TABS_REFRESH);
  };

  const ensureReviewMetaOnReaction = (reaction, state, isLocked) => {
    const prevChemkit = reaction?.chemkit && typeof reaction.chemkit === 'object' ? reaction.chemkit : {};
    const prevReview = prevChemkit.review && typeof prevChemkit.review === 'object' ? prevChemkit.review : {};
    const baseCanUpdate = typeof prevReview.base_can_update === 'boolean'
      ? prevReview.base_can_update
      : reaction.can_update === true;
    reaction.chemkit = {
      ...prevChemkit,
      version: 1,
      review: {
        ...prevReview,
        state,
        is_locked: isLocked,
        base_can_update: baseCanUpdate,
      },
    };
    return baseCanUpdate;
  };

  const setSampleLock = (sample, isLocked) => {
    if (!sample || typeof sample !== 'object') {
      return;
    }
    const sampleChemkit = sample.chemkit && typeof sample.chemkit === 'object' ? sample.chemkit : {};
    const reviewDependency = sampleChemkit.review_dependency && typeof sampleChemkit.review_dependency === 'object'
      ? sampleChemkit.review_dependency
      : {};
    const baseCanUpdate = typeof reviewDependency.base_can_update === 'boolean'
      ? reviewDependency.base_can_update
      : sample.can_update === true;
    sample.can_update = isLocked ? false : baseCanUpdate;
    sample.chemkit = {
      ...sampleChemkit,
      review_dependency: {
        ...reviewDependency,
        base_can_update: baseCanUpdate,
      },
    };
  };

  const setReactionLock = (reaction, isLocked, state) => {
    if (!reaction || reaction.type !== 'reaction') {
      return;
    }

    const baseCanUpdate = ensureReviewMetaOnReaction(reaction, state, isLocked);
    reaction.can_update = isLocked ? false : baseCanUpdate;

    const materialGroups = [
      reaction.starting_materials,
      reaction.reactants,
      reaction.solvents,
      reaction.purification_solvents,
      reaction.products,
    ];
    materialGroups.forEach((group) => {
      if (!Array.isArray(group)) {
        return;
      }
      group.forEach((sample) => setSampleLock(sample, isLocked));
    });
  };

  const lockTreeByReactionId = (reactionId, state = 'review_pending') => {
    let didMutate = false;
    const reactions = findLoadedEntities(reactionId, 'reaction');
    reactions.forEach((reaction) => {
      setReactionLock(reaction, true, normalizeReviewState(reaction?.chemkit?.review?.state) || 'review_pending');
      ensureReviewMetaOnReaction(reaction, normalizeReviewState(state) || 'review_pending', true);
      didMutate = true;
    });
    const sampleInfos = getSampleInfoForReactionId(reactionId);
    sampleInfos.forEach((sampleInfo) => {
      const samples = findLoadedEntities(sampleInfo.id, 'sample');
      samples.forEach((sample) => {
        setSampleLock(sample, true);
        didMutate = true;
      });
    });
    if (didMutate) {
      updateTabs();
    }
  };

  const unlockTreeByReactionId = (reactionId) => {
    let didMutate = false;
    const reactions = findLoadedEntities(reactionId, 'reaction');
    reactions.forEach((reaction) => {
      setReactionLock(reaction, false, 'open');
      didMutate = true;
    });
    const sampleInfos = getSampleInfoForReactionId(reactionId);
    sampleInfos.forEach((sampleInfo) => {
      const samples = findLoadedEntities(sampleInfo.id, 'sample');
      samples.forEach((sample) => {
        setSampleLock(sample, false);
        didMutate = true;
      });
    });
    if (didMutate) {
      updateTabs();
    }
  };

  return {
    lockTreeByReactionId,
    unlockTreeByReactionId,
    updateTabs,
  };
}
